// "Asistente de IA" en Mi perfil (D39, D46): cada persona conecta SU cuenta de Claude (plan Pro o Max) y/o de ChatGPT
// (su plan de ChatGPT), y elige con cual conversa. Claude: abre el enlace, autoriza y pega el codigo que muestra Claude.
// ChatGPT: abre el enlace y escribe alli el codigo que muestra esta pantalla; se conecta sola al autorizar. Las sesiones
// quedan en los programas oficiales del servidor, solo para esa persona; la Consola nunca ve credenciales.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  type CuentaIA,
  desconectarAsistente,
  elegirModelo,
  elegirProveedor,
  type EstadoAsistente,
  estadoAsistente,
  iniciarConexion,
  type Proveedor,
  PROVEEDORES,
  seguirConexion,
  terminarConexion,
  USO_MODELO,
} from '../api/asistente';
import { useSession } from '../auth/session';
import { Icon } from '../lib/icons';
import { Pill } from './bits';
import { toast } from './toasts';

export const useEstadoAsistente = () => useQuery({ queryKey: ['asistente', 'estado'], queryFn: estadoAsistente, staleTime: 60_000, retry: 1 });

export const NOMBRE_IA: Record<Proveedor, string> = { claude: 'Claude', chatgpt: 'ChatGPT' };
const PLAN_IA: Record<Proveedor, string> = { claude: 'plan Pro o Max', chatgpt: 'tu plan de ChatGPT' };

// Elegir el modelo de un asistente (se guarda en el servidor, para esta persona). Se usa aqui y en la conversacion.
export function useElegirModelo() {
  const qc = useQueryClient();
  return async (modelo: string, proveedor?: Proveedor) => {
    try {
      const r = await elegirModelo(modelo, proveedor);
      qc.setQueryData(['asistente', 'estado'], r);
      const c = proveedor ? r.cuentas?.[proveedor] : r;
      toast(`${NOMBRE_IA[proveedor ?? r.proveedor ?? 'claude']} usará ${c?.modelos.find((m) => m.id === modelo)?.nombre ?? modelo}.`);
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };
}

// Con cual asistente conversa la persona (Claude o ChatGPT).
export function useElegirProveedor() {
  const qc = useQueryClient();
  return async (proveedor: Proveedor) => {
    try {
      qc.setQueryData(['asistente', 'estado'], await elegirProveedor(proveedor));
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };
}

export function MiAsistente() {
  const { yo } = useSession();
  const e = useEstadoAsistente();
  const elegirIA = useElegirProveedor();
  if (!yo.miembroId) return null;
  const conectadas = PROVEEDORES.filter((p) => e.data?.cuentas?.[p]?.conectado);

  return (
    <section className="box">
      <h2 className="lbl">Asistente de IA</h2>
      <p className="sub" style={{ marginTop: 0 }}>
        El asistente usa <b>tu propia cuenta</b> de Claude o de ChatGPT para leer y ordenar tus casos. Solo ve lo que tú puedes ver en el CRM, no cambia nada, y tus cuentas son solo tuyas.
      </p>
      {e.isLoading ? (
        <p className="sub">Revisando…</p>
      ) : e.error || !e.data ? (
        <p className="sub">El asistente no responde en este momento.</p>
      ) : (
        <>
          {conectadas.length > 1 ? (
            <label className="form" style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
              Conversar con
              <select className="inp" value={e.data.proveedor ?? 'claude'} onChange={(ev) => void elegirIA(ev.target.value as Proveedor)} style={{ maxWidth: 260 }}>
                {conectadas.map((p) => (
                  <option key={p} value={p}>
                    {NOMBRE_IA[p]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {PROVEEDORES.map((p) => (
            <TarjetaCuenta key={p} proveedor={p} cuenta={e.data.cuentas?.[p] ?? (p === 'claude' ? e.data : null)} />
          ))}
        </>
      )}
    </section>
  );
}

function TarjetaCuenta({ proveedor, cuenta }: { proveedor: Proveedor; cuenta: CuentaIA | null }) {
  const qc = useQueryClient();
  const elegir = useElegirModelo();
  const elegirIA = useElegirProveedor();
  const nombre = NOMBRE_IA[proveedor];
  const [conexion, setConexion] = useState<{ url: string; codigo: string | null } | null>(null);
  const [codigo, setCodigo] = useState('');
  const [ocupado, setOcupado] = useState<'' | 'enlace' | 'codigo' | 'quitar'>('');

  const listo = async (r: EstadoAsistente) => {
    qc.setQueryData(['asistente', 'estado'], r);
    setConexion(null);
    toast(`Tu cuenta de ${nombre} quedó conectada.`);
    // La primera cuenta conectada pasa a ser con la que se conversa.
    if (!r.cuentas?.[r.proveedor ?? 'claude']?.conectado) await elegirIA(proveedor);
  };

  // ChatGPT: se pregunta cada 3 s hasta que la persona autoriza en la pagina de OpenAI.
  useEffect(() => {
    if (proveedor !== 'chatgpt' || !conexion) return;
    let vivo = true;
    const t = setInterval(async () => {
      try {
        const r = await seguirConexion('chatgpt');
        if (!vivo || r.resultado === 'esperando') return;
        clearInterval(t);
        if (r.resultado === 'conectado') await listo(r as EstadoAsistente);
        else {
          setConexion(null);
          toast(r.resultado === 'vencido' ? 'El código venció. Vuelve a tocar "Conectar".' : 'ChatGPT no confirmó la conexión. Revisa la ayuda de abajo y vuelve a intentarlo.', 'error');
        }
      } catch {
        /* se reintenta en la siguiente vuelta */
      }
    }, 3_000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conexion, proveedor]);

  const conectar = async () => {
    setOcupado('enlace');
    try {
      setConexion(await iniciarConexion(proveedor));
      setCodigo('');
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setOcupado('');
    }
  };
  const terminar = async () => {
    setOcupado('codigo');
    try {
      await listo(await terminarConexion(codigo));
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setOcupado('');
    }
  };
  const quitar = async () => {
    setOcupado('quitar');
    try {
      await desconectarAsistente(proveedor);
      await qc.invalidateQueries({ queryKey: ['asistente'] });
      toast(`Tu cuenta de ${nombre} se desconectó del CRM.`);
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setOcupado('');
    }
  };

  return (
    <div className="ia-cuenta" style={{ borderTop: '1px solid var(--line)', padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b>{nombre}</b>
        {cuenta?.conectado ? (
          <>
            <Pill tone="ok" dot>
              Conectada
            </Pill>
            <span className="sub">{[cuenta.correo, cuenta.plan && `plan ${cuenta.plan}`].filter(Boolean).join(' · ')}</span>
            <span style={{ flex: 1 }} />
            <button className="btn sm ghost" onClick={() => void quitar()} disabled={!!ocupado}>
              {ocupado === 'quitar' ? 'Desconectando…' : 'Desconectar'}
            </button>
          </>
        ) : (
          <span className="sub">Con {PLAN_IA[proveedor]}.</span>
        )}
      </div>
      {cuenta?.conectado ? (
        <label className="form" style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          Modelo
          <select className="inp" value={cuenta.modelo} onChange={(ev) => void elegir(ev.target.value, proveedor)} style={{ maxWidth: 260 }}>
            {cuenta.modelos.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
          <span className="sub">{USO_MODELO[cuenta.modelo] ?? ''}</span>
        </label>
      ) : conexion && proveedor === 'chatgpt' ? (
        <ol className="pasos" style={{ margin: '8px 0 0' }}>
          <li>
            <a className="btn sm primary" href={conexion.url} target="_blank" rel="noreferrer">
              <Icon name="external" /> Abrir ChatGPT
            </a>{' '}
            e inicia sesión con tu cuenta.
          </li>
          <li>
            Escribe este código allí:{' '}
            <b style={{ fontSize: 20, letterSpacing: 2, fontFamily: 'var(--mono, monospace)', userSelect: 'all' }}>{conexion.codigo}</b>{' '}
            <button type="button" className="btn sm ghost" onClick={() => void navigator.clipboard?.writeText(conexion.codigo ?? '').then(() => toast('Código copiado.'))}>
              Copiar
            </button>
          </li>
          <li className="sub">Esperando a que autorices… (el código vence en 15 minutos)</li>
          <li className="sub">
            Si ChatGPT dice que no está permitido: en ChatGPT ve a Configuración → Seguridad y activa el inicio de sesión con código de dispositivo (en planes de empresa lo activa el administrador).
          </li>
        </ol>
      ) : conexion ? (
        <ol className="pasos" style={{ margin: '8px 0 0' }}>
          <li>
            <a className="btn sm primary" href={conexion.url} target="_blank" rel="noreferrer">
              <Icon name="external" /> Abrir Claude y autorizar
            </a>
          </li>
          <li>Claude te muestra un código. Cópialo completo y pégalo aquí:</li>
          <li>
            <form className="form" style={{ display: 'flex', gap: 8 }} onSubmit={(ev) => (ev.preventDefault(), void terminar())}>
              <input className="inp" value={codigo} onChange={(ev) => setCodigo(ev.target.value)} placeholder="Código de Claude" aria-label="Código de Claude" autoComplete="one-time-code" spellCheck={false} />
              <button className="btn primary" disabled={!codigo.trim() || !!ocupado}>
                {ocupado === 'codigo' ? 'Conectando…' : 'Terminar'}
              </button>
            </form>
          </li>
        </ol>
      ) : (
        <button className="btn sm primary" style={{ marginTop: 8 }} onClick={() => void conectar()} disabled={!!ocupado}>
          {ocupado === 'enlace' ? 'Preparando…' : `Conectar mi cuenta de ${nombre}`}
        </button>
      )}
    </div>
  );
}
